// ============================================================================
// Ideas Hooks — data fetching and state management for Ideas feature
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import type { Idea, IdeaConfig, GenerationStatus } from '../types';

// ── useIdeas — list with filters ────────────────────────────────────────

interface UseIdeasParams {
  category?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useIdeas(params: UseIdeasParams = {}) {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();

  const fetchIdeas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.listIdeas({
        ...params,
        limit: 50,
      });
      setIdeas(result.items);
      setNextToken(result.nextToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, [params.category, params.status, params.search, params.sortBy, params.sortOrder, params.dateFrom, params.dateTo]);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  return { ideas, loading, error, nextToken, refetch: fetchIdeas };
}

// ── useIdea — single idea detail ────────────────────────────────────────

export function useIdea(ideaId: string | null) {
  const [idea, setIdea] = useState<Idea | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIdea = useCallback(async () => {
    if (!ideaId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getIdea(ideaId);
      setIdea(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load idea');
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  useEffect(() => {
    if (ideaId) {
      fetchIdea();
    } else {
      setIdea(null);
    }
  }, [fetchIdea, ideaId]);

  return { idea, loading, error, refetch: fetchIdea };
}

// ── useIdeaConfig — get and update config ───────────────────────────────

export function useIdeaConfig() {
  const [config, setConfig] = useState<IdeaConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.getIdeaConfig();
      setConfig(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = useCallback(async (data: Partial<IdeaConfig>) => {
    try {
      const result = await api.updateIdeaConfig(data);
      setConfig(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update config');
      throw err;
    }
  }, []);

  return { config, loading, error, updateConfig, refetch: fetchConfig };
}

// ── useGenerationStatus — polls generation status ───────────────────────

export function useGenerationStatus(pollInterval = 3000) {
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await api.getGenerationStatus();
      setStatus(result);
      return result;
    } catch {
      // Silently fail for polling
      return null;
    }
  }, []);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    fetchStatus(); // Immediate fetch
    intervalRef.current = setInterval(fetchStatus, pollInterval);
  }, [fetchStatus, pollInterval]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Auto-stop when generation completes
  useEffect(() => {
    if (status && !status.running && intervalRef.current) {
      // Give a brief delay before stopping to show the final status
      setTimeout(stopPolling, 2000);
    }
  }, [status, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return { status, fetchStatus, startPolling, stopPolling };
}
